import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { parseStatusColorConfig } from '../../utils/statusColor';

export type SupportAgreementType = 'MONTHLY' | 'AT_CALL' | 'HOUR_PACKAGE';

export interface SupportAgreement {
  id: number;
  clientId: number;
  agreementType: SupportAgreementType;
  agreementTypeId?: number;
  categoryId?: number;
  description: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  notes?: string;
  // MONTHLY fields
  monthlyFee?: number;
  includedHours?: number;
  overageRate?: number;
  // AT_CALL fields
  hourlyRate?: number;
  minimumHours?: number;
  // HOUR_PACKAGE fields
  totalMinutes?: number;
  usedMinutes?: number;
  packagePrice?: number;
  // Renewal/Termination
  autoRenew?: boolean;
  terminationDate?: string;
  terminationReason?: string;
  // Payment tracking
  totalPaid?: number;
  totalDue?: number;
  // Tracking
  createdById?: number;
  createdAt?: string;
  // Related data from includes
  client?: {
    clientId: number;
    clientName?: string;
  };
  agreementTypeDescr?: {
    pst_descr?: string;
    pst_color?: string;
  };
  categoryDescr?: {
    psc_descr?: string;
  };
  supportTasks?: Array<{
    minutesUsed?: number;
    billedAmount?: number;
  }>;
  invoices?: Array<{
    invoiceId: number;
    invoiceAmount?: number;
    InvoiceDate?: string;
  }>;
  renewalReminders?: Array<{
    id: number;
    contactedAt: string;
    responseStatus?: string | null;
    responseNote?: string | null;
    responseAt?: string | null;
  }>;
  totalMinutesUsed?: number;
}

export interface SupportAgreementsResponse {
  records: SupportAgreement[];
  activeCount?: number;
}

export const useSupportAgreements = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }

  const { data, error, isLoading, mutate } = useSWR<SupportAgreementsResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const agreements = data?.records || [];

  return {
    data: agreements,
    agreements: agreements,
    total: agreements.length,
    activeCount: data?.activeCount || 0,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSupportAgreement = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupportAgreement>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    agreement: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/crm-clients',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    clients: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get service types for select dropdown
export const useServiceTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-service-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    serviceTypes: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((type) => ({
      value: String(type.id),
      label: type.pst_descr || `Service Type #${type.id}`,
      icon: React.createElement('span', {
        className: 'inline-block h-2.5 w-2.5 rounded-full border border-black/10',
        style: { backgroundColor: parseStatusColorConfig(type.pst_color).backgroundColor },
      }),
    })),
  };
};

// Hook to get service categories for select dropdown
export const useServiceCategoriesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-service-category',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    serviceCategories: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((category) => ({
      value: String(category.id),
      label: category.psc_descr || `Category #${category.id}`,
    })),
  };
};
